import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, input, signal, untracked, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EvidenceEnforcement, resolveEvidenceConfig, resolveEvidenceLevel, TaskEvidenceKind, TaskEvidenceOptions } from '@jms/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { SelectComponent } from '@workbuddy/ui/v2/fields/select';
import { EVIDENCE_COLLECTION_LABELS, EVIDENCE_ENFORCEMENT_OPTIONS } from './evidence.enforcement.options';
import { evidenceEnforcementControlBridgeStyles, evidenceEnforcementControlStyleId } from './evidence.enforcement.control.theme';

type EvidenceEnforcementMap = NonNullable<TaskEvidenceOptions['evidenceEnforcement']>;

@Component({
  selector: 'evidence-enforcement-control',
  templateUrl: './evidence.enforcement.control.component.html',
  styleUrls: ['./evidence.enforcement.control.component.less'],
  standalone: true,
  imports: [FormsModule, SelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EvidenceEnforcementControlComponent), multi: true }],
})
export class EvidenceEnforcementControlComponent implements ControlValueAccessor {

  /** The enabled collections decide the rows; the control owns only the level map. */
  public readonly $evidence = input.required<TaskEvidenceOptions>({ alias: 'evidence' });
  public readonly $readonlyInput = input(false, { alias: 'readonly' });

  private readonly document = inject(DOCUMENT);
  private readonly $value = signal<EvidenceEnforcementMap | null>(null);

  protected readonly $disabled = signal(false);
  protected readonly $readonly = computed(() => this.$disabled() || this.$readonlyInput());
  protected readonly options = EVIDENCE_ENFORCEMENT_OPTIONS;
  protected readonly $rows = computed(() => resolveEvidenceConfig({ ...this.$evidence(), evidenceEnforcement: this.$value() ?? undefined })
    .map(config => ({ ...config, label: EVIDENCE_COLLECTION_LABELS[config.kind] })));

  private onChange: (value: EvidenceEnforcementMap) => void = () => {};
  private onTouched: () => void = () => {};
  // Undefined until the first evidence read after a load, so loading a task never counts as turning its sections on.
  private enabledBefore: ReadonlySet<TaskEvidenceKind> | undefined;

  public constructor() {

    DesignSystem.Mount(this.document, evidenceEnforcementControlStyleId, evidenceEnforcementControlBridgeStyles);
    effect(() => this.storeLevelsForEnabled(this.$evidence()));
  }

  public writeValue(value: EvidenceEnforcementMap | null): void {

    this.$value.set(value ?? null);
    this.enabledBefore = undefined;
  }

  public registerOnChange(fn: (value: EvidenceEnforcementMap) => void): void {

    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {

    this.$disabled.set(isDisabled);
  }

  // A section turned on in the host form stores the level its row shows at that moment (Optional, or Required on a gated task), so a saved task never leaves the gate to guess.
  private storeLevelsForEnabled(evidence: TaskEvidenceOptions): void {

    const enabled = new Set(resolveEvidenceConfig(evidence).map(config => config.kind));
    const before = this.enabledBefore;
    this.enabledBefore = enabled;
    if (!before) return;

    const stored = untracked(this.$value) ?? {};
    const added = [...enabled].filter(kind => !before.has(kind) && !stored[kind]);
    if (!added.length) return;

    const next = { ...stored, ...Object.fromEntries(added.map(kind => [kind, resolveEvidenceLevel({ ...evidence, evidenceEnforcement: stored }, kind)])) };
    this.$value.set(next);
    this.onChange(next);
  }

  protected onSelect(kind: TaskEvidenceKind, level: EvidenceEnforcement): void {

    const next = { ...this.$value(), [kind]: level };

    this.$value.set(next);
    this.onTouched();
    this.onChange(next);
  }
}
